import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X, Clock, StickyNote } from "lucide-react";

export type FastStatus = "present" | "unexcused_absence" | "excused_absence";

const PILLS: { st: FastStatus; icon: typeof Check; key: string; fg: string; bg: string }[] = [
    { st: "present", icon: Check, key: "fast_att.present", fg: "var(--success-fg)", bg: "var(--success-bg)" },
    { st: "unexcused_absence", icon: X, key: "fast_att.absent", fg: "var(--error-fg)", bg: "var(--error-bg)" },
    { st: "excused_absence", icon: Clock, key: "fast_att.excused", fg: "var(--warning-fg)", bg: "var(--warning-bg)" },
];

export const isPresent = (st: string) => st === "present" || st === "late";

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

export function AttendanceStudentCard({ id, name, phone, status, note, disabled, onStatus, onNote }: {
    id: string; name: string; phone?: string | null; status: string; note: string; disabled: boolean;
    onStatus: (st: FastStatus) => void; onNote: (v: string) => void;
}) {
    const { t } = useTranslation();
    const [showNote, setShowNote] = useState(!!note);
    const active = PILLS.find((p) => (p.st === "present" ? isPresent(status) : p.st === status));
    return (
        <div className="rounded-xl border p-3 transition-colors" data-testid={`fast-att-card-${id}`} data-status={status}
            style={{ borderColor: active ? active.fg : "var(--border)", background: "var(--card)" }}>
            <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold"
                    style={{ background: active?.bg || "var(--input)", color: active?.fg || "var(--text-muted)" }}>{initials(name)}</div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{name || "—"}</div>
                    <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                        {phone || "\u00a0"}{status === "late" && <span className="ml-1">· {t("attendance.status.late")}</span>}
                    </div>
                </div>
                <button type="button" data-testid={`fast-att-note-toggle-${id}`} onClick={() => setShowNote((v) => !v)} title={t("fast_att.note")}
                    className="h-10 w-10 shrink-0 rounded-lg flex items-center justify-center" style={{ color: note ? "var(--primary)" : "var(--text-muted)", background: "var(--input)" }}>
                    <StickyNote size={16} />
                </button>
            </div>
            <div className="grid grid-cols-3 gap-2" role="radiogroup">
                {PILLS.map(({ st, icon: Icon, key, fg, bg }) => {
                    const on = st === "present" ? isPresent(status) : status === st;
                    return (
                        <button key={st} type="button" role="radio" aria-checked={on} disabled={disabled} data-testid={`fast-att-${id}-${st}`}
                            onClick={() => onStatus(st)}
                            className="min-h-[48px] rounded-lg border-2 text-xs sm:text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-[background-color,border-color,transform] active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-70"
                            style={{ borderColor: on ? fg : "var(--border)", background: on ? bg : "transparent", color: on ? fg : "var(--text-muted)" }}>
                            <Icon size={16} strokeWidth={on ? 3 : 2} /> <span className="truncate">{t(key)}</span>
                        </button>
                    );
                })}
            </div>
            {showNote && (
                <textarea data-testid={`fast-att-note-${id}`} value={note} disabled={disabled} maxLength={500} rows={2}
                    placeholder={t("fast_att.note_placeholder")} onChange={(e) => onNote(e.target.value)}
                    className="surface-input mt-2 w-full px-3 py-2 text-sm" />
            )}
        </div>
    );
}
